'use client'

import { useFeatures } from '@/src/hooks/useFeatures'
import { useRouter } from '@/src/i18n/navigation'
import { HOLIDAYS_NAV_PATH } from '@/src/utils/holidays'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'

import LobTabs, { LobTabItem } from './lobTabs'


interface LobTabsWrapperProps {
    /** Resolved server-side by the same rule as the flights homepage, so both landing
     *  pages offer the same verticals on a given domain: AE gets Visa, the rest get
     *  Flights + Hotels where the variant enables it. */
    showVisaTab: boolean
    showFlightsHotelsTab: boolean
    /** Server-resolved via showHolidaysServer() */
    showHolidaysTab: boolean
    /** The page this strip is rendered on; its tab is the active one and does not navigate. */
    activeKey?: 'hotels' | 'holidays'
}

// Logic layer — resolves the visible tabs and routes each one. Hotels is always active
// here; Flights, Holidays and Experience are internal routes, Visa and Flights + Hotels are still edge-served.
const LobTabsWrapper: React.FC<LobTabsWrapperProps> = ({
    showVisaTab,
    showFlightsHotelsTab,
    showHolidaysTab,
    activeKey = 'hotels'
}) => {
    const t = useTranslations('header')
    const locale = useLocale()
    const router = useRouter()
    const { EXPERIENCE } = useFeatures()

    const tabs = useMemo<LobTabItem[]>(() => {
        const items: LobTabItem[] = [
            { key: 'flights', label: t('flights') },
            { key: 'hotels', label: t('hotels') }
        ]
        if (showVisaTab) items.push({ key: 'visa', label: t('visa') })
        else if (showFlightsHotelsTab) items.push({ key: 'fph', label: t('fph') })
        if (showHolidaysTab) items.push({ key: 'holidays', label: t('holidays'), badge: t('new'), highlight: true })
        if (EXPERIENCE) items.push({ key: 'experience', label: t('experience') })
        return items
    }, [t, showVisaTab, showFlightsHotelsTab, showHolidaysTab, EXPERIENCE])

    const activeIndex = useMemo(
        () => Math.max(tabs.findIndex(tab => tab.key === activeKey), 0),
        [tabs, activeKey]
    )

    const handleTabChange = useCallback(
        (index: number) => {
            const key = tabs[index]?.key
            // The tab for the page we are on is a no-op rather than a reload of the same route.
            if (key === activeKey) return
            if (key === 'holidays') {
                router.push(HOLIDAYS_NAV_PATH)
                return
            }
            if (key === 'hotels') {
                router.push('/hotels')
                return
            }
            if (key === 'flights' || key === 'experience') {
                router.push(`/${key}`)
                return
            }
            // Neither vertical has a route in this app, so both stay hard navigations —
            // relative, so regional and Cleartrip domains resolve against themselves.
            if (key === 'visa') window.location.href = '/visa'
            if (key === 'fph') window.location.href = `/${locale}/flights-hotels`
        },
        [tabs, router, locale, activeKey]
    )

    return (
        <LobTabs
            tabs={tabs}
            activeIndex={activeIndex}
            onTabChange={handleTabChange}
            ariaLabel="line of business tabs"
        />
    )
}

export { LobTabsWrapper }
export default LobTabsWrapper
